import { call } from "./tasks";
import { tx } from "../i18n";

/* ══ 이슈 정리가 읽는 문 — `crates/api/src/tidy.rs` ══
   시안 `t-in-tidy-design` 채택안 A(Figma 52-3) · 공통 프레임 55-2 ④ 가 요구한 것만 있다.
   이슈 쪽 사실(제목·상태·남길 쪽)은 이 응답에 없다 — 화면이 이미 든 `GET /tasks` 로 붙인다. */

export type TidyKind = "duplicate" | "already-done" | "stale-in-progress" | "design-first";
export type ProposalStatus = "open" | "applied" | "dismissed";

/** `GET /tidy/proposals` 의 한 줄. `kind` 는 **모르는 값이 올 수 있다**(서버가 갈래를 늘린 날) —
 *  그래서 string 이고, 화면은 모르면 코드 그대로 그린다. */
export type Proposal = {
  id: string;
  taskId: string;
  kind: string;
  refTaskId: string | null;
  reason: string;
  status: ProposalStatus;
  batchId: string | null;
  createdBy: string;
  createdAt: string;
  resolvedAt: string | null;
  resolvedBy: string | null;
};

/** 적용의 건너뜀. **상태가 아니다** — 제안은 open 그대로 남는다. */
export type Skip = { code: string; message?: string; holder?: string };
export type ApplyResult =
  | { applied: true; proposalId: string }
  | { applied: false; proposalId: string; skipped: Skip };

/** `GET /tidy/state` — 범위 줄. `lastSweepAt` 이 null 이면 **기록 없음**이지 0 이 아니다. */
export type TidyState = {
  anchor: { id: string; title: string; status: string; requestedAt: string | null; running: boolean } | null;
  lastSweepAt: string | null;
};

const post = <T>(path: string) =>
  call<T>(path, { method: "POST", headers: { "content-type": "application/json" }, body: "{}" });

export const tidyApi = {
  /** 상태 거르개는 화면 안에서 한다 — 한 번에 전부 받아 사이드바 수를 함께 센다. */
  list: () => call<Proposal[]>(`/tidy/proposals?status=all`),
  state: () => call<TidyState>(`/tidy/state`),
  request: () => post<TidyState>(`/tidy/request`),
  /** **건별로 부른다** — 한 건이 건너뛰어져도 나머지가 간다. */
  apply: (id: string) => post<ApplyResult>(`/tidy/proposals/${encodeURIComponent(id)}/apply`),
  dismiss: (id: string) => post<Proposal>(`/tidy/proposals/${encodeURIComponent(id)}/dismiss`),
};

type Tone = "muted" | "accent" | "success" | "warning" | "error" | "info";

/** 갈래 표 — 글리프 · 글자 · 톤 · 「적용하면 →」. **색만으로 가르지 않는다.** */
export const KIND: Record<TidyKind, { glyph: string; label: string; tone: Tone; applies: string }> = {
  duplicate: { glyph: "⧉", label: tx("tidy.kind.duplicate"), tone: "info", applies: tx("tidy.applies.duplicate") },
  "already-done": { glyph: "✓", label: tx("tidy.kind.alreadyDone"), tone: "success", applies: tx("tidy.applies.alreadyDone") },
  "stale-in-progress": { glyph: "⏸", label: tx("alarm.kind.stale"), tone: "warning", applies: tx("tidy.applies.stale") },
  "design-first": { glyph: "✎", label: tx("tidy.kind.designFirst"), tone: "accent", applies: tx("tidy.applies.designFirst") },
};
export const KIND_KEYS = Object.keys(KIND) as TidyKind[];
export const isKnownKind = (k: string): k is TidyKind => k in KIND;

export const PROPOSAL_STATUS: Record<ProposalStatus, { glyph: string; label: string; tone: Tone }> = {
  open: { glyph: "○", label: tx("tidy.status.open"), tone: "muted" },
  applied: { glyph: "●", label: tx("tidy.status.applied"), tone: "success" },
  dismissed: { glyph: "⊘", label: tx("tidy.status.dismissed"), tone: "muted" },
};

/** 건너뜀 사유 문장 — **화면이 든다**. 순서는 서버 `message` > 이 표 > **코드 그대로**.
 *  서버가 새 사유를 보내기 시작해도 화면이 침묵하지 않는다. */
export const SKIP_KEY = {
  held: "tidy.skip.held",
  "invalid transition": "tidy.skip.invalidTransition",
  "parent status is derived": "tidy.skip.parentDerived",
  "dependency cycle": "tidy.skip.depCycle",
  "dependency not found": "tidy.skip.depNotFound",
  "not stale": "tidy.skip.notStale",
  "task not found": "tidy.skip.taskNotFound",
  "ref not found": "tidy.skip.refNotFound",
  "dependencies not done": "tidy.skip.depsNotDone",
} as const;

export function skipText(s: Skip): string {
  const key = (SKIP_KEY as Record<string, (typeof SKIP_KEY)[keyof typeof SKIP_KEY] | undefined>)[s.code];
  return s.message ?? (key ? tx(key) : s.code);
}
